import { useAuth } from '@clerk/clerk-react';
import { useEffect, type PropsWithChildren } from 'react';

import { setTokenGetter } from '@core/api/tokenBridge';

import { AppSplash } from './AppSplash';

/**
 * Hands Clerk's `getToken` to the axios interceptors, which run outside React.
 *
 * Rendering a splash until `isLoaded` is not only cosmetic: it keeps the whole tree from
 * mounting -- and firing queries -- before a token is obtainable. The bridge's own
 * readiness promise covers the race, but not mounting early is cheaper than resolving it.
 *
 * The alternative is constructing the Clerk instance yourself
 * (`new Clerk(key)` + `<ClerkProvider Clerk={clerk}>`) and importing it in the
 * interceptor. Conceptually tidier, but it forces an eager load of the whole Clerk
 * bundle before first paint.
 */
export function ClerkTokenBridge({ children }: PropsWithChildren) {
  const { getToken, isLoaded } = useAuth();

  useEffect(() => {
    setTokenGetter(getToken);
    return () => setTokenGetter(null);
  }, [getToken]);

  if (!isLoaded) return <AppSplash />;
  return <>{children}</>;
}
